import { CatalogBuilder } from "./catalog.builder";
import { updateItemStyle } from "./catalog.item";
import { isBoundContainer } from "./catalog.products";
import { getPageGrid, updatePage } from "./catalog.page";
import { CatalogPageRef, CatalogSection, PageEntry } from "./catalogs";

export type CatalogStructure = {
  entries: PageEntry[];
  viewPages: CatalogPageRef[];
  productPages: Record<string, number[]>; // productId -> view pageIds
};

export const refreshCatalogStructure = (builder: CatalogBuilder) => {
  const entries: PageEntry[] = [];
  const productPages: Record<string, number[]> = {};

  const sections = [...builder.sections];
  builder.sections = [];

  let viewPageIndex = 0;
  let section: CatalogSection = null;

  for (let i = 0; i < builder.pages.length; i++) {
    const page = builder.pages[i];

    if (page.pageItem?.di.Title && page.pageItem?.di.PageType !== "toc") {
      section = sections.find(
        (s) => s.id === page.pageItem.id || s.title === page.pageItem?.di.Title
      );
      section = {
        id: page.pageItem.id,
        title: page.pageItem?.di.Title,
        expanded: section?.expanded ?? true,
        start: page.pageId,
        end: page.pageId,
      };
      builder.sections.push(section);
    }

    if (section && !section.expanded) {
      let pages = [];
      const pi = builder.pages.findIndex(
        (p, n) => n > i && p.pageItem?.di.Title
      );
      const end = pi >= 0 ? pi - 1 : builder.pages.length - 1;
      pages = builder.pages.slice(page.pageId, end + 1);
      entries.push({
        id: viewPageIndex,
        range: {
          start: page.pageId,
          end,
          viewStart: viewPageIndex,
          viewEnd: viewPageIndex + pages.length - 1,
        },
        pages,
        section,
        multiple: end > page.pageId,
      });
      i = end;
      viewPageIndex += pages.length;
      section.end = end;
      continue;
    }

    if (section) section.end = page.pageId;

    const subcatalog = page.pageItem?.info.subcatalog;
    if (subcatalog) {
      const pages = subcatalog.pages ?? [];
      if (!pages.length) pages.push(page);
      entries.push({
        id: viewPageIndex,
        subcatalog,
        range: {
          start: page.pageId,
          end: page.pageId,
          viewStart: viewPageIndex,
          viewEnd: viewPageIndex + pages.length - 1,
        },
        pages: pages,
        multiple: true,
        readOnly: true,
        originalPage: page,
        section,
      });
      viewPageIndex += pages.length;
      continue;
    }

    const grid = getPageGrid(builder, page.pageId);
    if (grid) {
      let pages = [];
      // A BOUND grid lives on one config page and expands into its resolved preview. A grid
      // with an explicit list already occupies real pages: reading a leftover binding here
      // gave every one of those pages its own full-preview entry, so a 3-page grid rendered
      // three times over and the page count tripled.
      if (isBoundContainer(grid)) {
        pages = grid.preview.pages;
        if (!pages.length) pages.push(page);
        entries.push({
          id: viewPageIndex,
          range: {
            start: page.pageId,
            end: page.pageId,
            viewStart: viewPageIndex,
            viewEnd: viewPageIndex + pages.length - 1,
          },
          pages: pages,
          grid,
          multiple: pages.length > 1,
          readOnly: true,
          originalPage: page,
          section,
        });
      } else {
        const end = Math.min(
          grid.expanded ? page.pageId : grid.end,
          builder.pages.length - 1
        );
        if (
          page.pageId <= end &&
          (grid.begin === page.pageId || grid.expanded)
        ) {
          pages = builder.pages.slice(page.pageId, end + 1);
          entries.push({
            id: viewPageIndex,
            range: {
              start: page.pageId,
              end,
              viewStart: viewPageIndex,
              viewEnd: viewPageIndex + pages.length - 1,
            },
            pages,
            grid,
            multiple: end > page.pageId,
            section,
          });
        }
        i = end;
      }
      viewPageIndex += pages.length;
    } else {
      entries.push({
        id: viewPageIndex,
        range: {
          start: page.pageId,
          end: page.pageId,
          viewStart: viewPageIndex,
          viewEnd: viewPageIndex,
        },
        pages: [page],
        section,
      });
      viewPageIndex++;
    }
  }

  const viewPages = entries
    .map((e) =>
      e.pages.map((p) => ({
        ...p,
        entry: e,
        pageId: e.originalPage?.pageId ?? p.pageId,
      }))
    )
    .flat()
    .map((p, i) => ({ ...p, viewPageId: i }));

  builder.structure = { entries, viewPages, productPages };

  viewPages.forEach((p) => {
    updatePage(builder, p);
    p.items.forEach((item) => {
      // Re-lay-out each item now that its view page (and thus header/footer
      // context) is known. Grids size their cards in refreshGrids() before the
      // structure exists, when getDimensions()->getViewPage() falls back to the
      // cover page (no header). Without this pass the page-reserved header is
      // not accounted for and bottom card elements (e.g. price) overflow the
      // page and get clipped until the next full re-init (expand/edit).
      updateItemStyle(builder, item, p);
      if (item.di.ItemId) {
        if (!productPages[item.di.ItemId]) {
          productPages[item.di.ItemId] = [];
        }
        productPages[item.di.ItemId].push(p.viewPageId);
      }
    });
  });
};

export const getOriginalPages = (entries: PageEntry[]): CatalogPageRef[] => {
  return entries
    .map((e) => (e.originalPage ? [e.originalPage] : e.pages))
    .flat();
};

export const setActivePageId = (
  builder: CatalogBuilder,
  pageId: number,
  isView?: boolean
) => {
  if (isView) {
    if (pageId < 0) {
      pageId = 0;
    }
    if (pageId >= builder.structure.viewPages.length) {
      pageId = builder.structure.viewPages.length - 1;
    }
    builder.context.viewPageIndex = pageId;
    builder.context.activePageId =
      builder.structure.viewPages[pageId]?.entry?.range.start ?? 0;
  } else {
    if (pageId < 0 || pageId >= builder.pages.length) {
      pageId = 0;
    }
    builder.context.activePageId = pageId ?? 0;
    const i = builder.structure.viewPages.findIndex((p) => pageId === p.pageId);
    builder.context.viewPageIndex = i >= 0 ? i : pageId;
  }
};
